import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { getAppConfig } from 'app/getConfig';
import { FeatureFlags } from 'config/featureFlags';
import { authApi } from 'container/authApi';
import {
  selectUserId,
  selectUserEmail,
  selectUserFirstName,
  selectUserLastName,
  selectUserExpiration,
} from 'reduxStore/auth/selectors';
import { useRescanIntercom } from 'shared/hooks/useRescanIntercom';
import { useWBIntercom } from '@workbuzz/shared-ui';

export const useInitializeIntercom = () => {
  const { INTERCOM_APP_ID, INTERCOM_MESSENGER_STYLE_ID } = getAppConfig();
  const isIntercomEnabled = useFeatureIsOn(FeatureFlags.INTERCOM);
  const userId = useSelector(selectUserId);
  const email = useSelector(selectUserEmail);
  const firstName = useSelector(selectUserFirstName);
  const lastName = useSelector(selectUserLastName);
  const expiration = useSelector(selectUserExpiration);

  const enabled = isIntercomEnabled && Boolean(userId);

  const [intercomUserJwt, setIntercomUserJwt] = useState<string>();

  useEffect(() => {
    if (!enabled) {
      setIntercomUserJwt(undefined);
      return;
    }

    let active = true;
    authApi
      .fetchIntercomToken()
      .then((token) => active && setIntercomUserJwt(token))
      .catch(() => active && setIntercomUserJwt(undefined));

    return () => {
      active = false;
    };
    // re-fetch when the session (and therefore the token expiry) changes
  }, [enabled, expiration]);

  const name = [firstName, lastName].filter(Boolean).join(' ') || undefined;

  const isBooted = enabled && Boolean(intercomUserJwt);

  // boot only once the identity JWT is present, so the Messenger never runs unverified
  useWBIntercom({
    enabled: isBooted,
    appId: INTERCOM_APP_ID,
    messengerStyleId: INTERCOM_MESSENGER_STYLE_ID || undefined,
    user: userId
      ? {
          userId,
          email,
          name,
          userJwt: intercomUserJwt,
        }
      : undefined,
  });

  useRescanIntercom(isBooted);
};
